import { Menu, MenuProps } from 'antd';
import { isEmpty, map } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getRealPath } from '../helper';
import routesMap from '../routesMap';
import { useNavs } from './useNav';
import { useRoutes } from './useRoutes';
import { brandName } from '../../theme/tokens';

type MenuItem = Required<MenuProps>['items'][number];

const getItem = (
  key: React.Key,
  label: React.ReactNode,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group'
): MenuItem => {
  return { key, icon, children, label, type } as MenuItem;
};

interface AppSidebarProps {
  isMobile?: boolean;
  onNavigate?: () => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ isMobile, onNavigate }) => {
  const navs = useNavs();
  const routes = useRoutes();
  const location = useLocation();

  const [selectedKeys, setSelectedKeys] = useState<string[] | undefined>();
  const [openKeys, setOpenKeys] = useState<string[] | undefined>();

  useEffect(() => {
    const pathNames = location.pathname.split('/').filter((x) => x);

    if (pathNames.length) {
      const pathName = `/${pathNames.join('/')}`;
      const findRoute = routes.find((route) => {
        return route.path !== routesMap.HOME && pathName.indexOf(getRealPath(route.path)) !== -1;
      });      

      if (findRoute) {
        setOpenKeys(findRoute.openKey);
        setSelectedKeys(findRoute.activeKey);
      }
    }

    if (location.pathname === routesMap.HOME) {
      setSelectedKeys([routesMap.HOME]);
    }
  }, [location.pathname, routes]);

  const items = useMemo((): MenuItem[] => {
    const items: MenuItem[] = [];

    navs.forEach((nav: any) => {
      const Icon = nav.icon ? <nav.icon size={20} /> : undefined;

      if (isEmpty(nav.items)) {
        items.push(getItem(nav.key, <Link to={nav.key}>{nav.name}</Link>, Icon));
      } else {
        const itemsChild1 = map(nav.items, (nav1) => {
          return getItem(nav1.key, <Link to={nav1.key}>{nav1.name}</Link>);
        });

        items.push(getItem(nav.key, nav.name, Icon, itemsChild1));
      }
    });

    return items;
  }, [navs]);

  const renderLogo = (): JSX.Element => {
    return (
      <div
        className="flex flex-row items-center gap-3"
        style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'rgba(255,255,255,0.12)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          H
        </div>
        <div style={{ lineHeight: 1.3, overflow: 'hidden' }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}>
            {brandName}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>Quản lý spa</div>
        </div>
      </div>
    );
  };

  const renderMenu = (): JSX.Element => {
    return (
      <Menu
        theme="dark"
        items={items}
        openKeys={openKeys}
        selectedKeys={selectedKeys}
        className="font-medium select-none"
        style={{ borderInlineEnd: 'none', paddingTop: 8 }}
        onSelect={(info): void => {
          setSelectedKeys(info.selectedKeys);
          if (isMobile && onNavigate) onNavigate();
        }}
        onOpenChange={(keys): void => setOpenKeys(keys)}
      />
    );
  };

  return (
    <div className="flex flex-col w-[100%] h-[100%]" style={{ background: '#3A2430' }}>
      {renderLogo()}
      <div className="w-[100%]">
        <div className="object-contain">{renderMenu()}</div>
      </div>
    </div>
  );
};

export default AppSidebar;